import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {useViewedContentStore} from '@/ts/store/viewed-content-store';
import type {UuidDTO} from '@/ts/types/dto/sign-in-dto';

/**
 * Reports a piece of content as presented: put in front of the reader without being asked for
 * (IPD-3114). The welcome video is the only caller — it plays itself, and the stamp is what stops
 * the next visit starting it again.
 *
 * Deliberately not the same report as `useWatchedReport`. A video that played says nothing about
 * whether anyone watched it, and the two decide different things: a watch retires the tile and
 * feeds the watch metric, a presentation only silences the autoplay.
 *
 * Fire-and-forget for the same reason as the watch: nothing on screen depends on it, and a failed
 * call must not interrupt someone. A failure only means the video starts itself once more.
 *
 * `hasReportedPresented` is handed out alongside because the backend flag alone cannot answer the
 * question within one session: it is read from a payload, and the payload that says "autoplay" is
 * fetched again — and the player rebuilt — while the report of the first one is still in flight.
 */
export const usePresentedReport = (): {
  reportPresented(contentId: UuidDTO): void;
  hasReportedPresented(contentId: UuidDTO): boolean;
} => {
  const api = useApi();
  const {error} = useLogger();
  const store = useViewedContentStore();

  const reportPresented = (contentId: UuidDTO): void => {
    if (!contentId || store.hasReportedPresented(contentId)) {
      return;
    }
    store.markReportedPresented(contentId);

    api.me.postBlockContentPresented(contentId).catch((e: unknown) => {
      store.forgetReportedPresented(contentId);
      error(`Reporting content ${contentId} as presented failed: ${String(e)}`)();
    });
  };

  return {reportPresented, hasReportedPresented: store.hasReportedPresented};
};
